import React, { useContext } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModalContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../components/ServicePageLayout.css';

const CopyrightRegistration = () => {
  const { openModal } = useContext(ModalContext);
  const [openFaq, setOpenFaq] = React.useState(null);

  const heroData = {
    eyebrow: 'COPYRIGHT REGISTRATION',
    title: 'Safeguard Your Creative Works',
    description: 'Protect your original literary, artistic, musical, and digital creations with comprehensive copyright registration services.',
    backgroundImage: 'https://images.pexels.com/photos/7641842/pexels-photo-7641842.jpeg',
    showForm: false,
    ctaText: 'Register Copyright',
    ctaLink: '#pricing'
  };

  const processSteps = [
    {
      title: 'Information Collection',
      description: 'We collect detailed information about your creative work, including authorship details, creation date, and publication status. Our copyright specialists review your submission to ensure all necessary information is captured for proper registration with the U.S. Copyright Office.'
    },
    {
      title: 'Documentation Preparation',
      description: 'Our team prepares your complete copyright application with accurate work classification, proper authorship attribution, and all required supporting documentation. We ensure your application meets all Copyright Office requirements for electronic filing.'
    },
    {
      title: 'Application and Submission',
      description: 'We electronically file your copyright application directly with the U.S. Copyright Office, providing you with immediate confirmation of filing. Your official registration certificate will be mailed to you from the Copyright Office upon approval, typically within 3-8 months.'
    }
  ];

  const pricingPlans = [
    { 
      name: 'Gold', 
      price: '99', 
      features: [
        'Professional Preparation of your federal copyright application',
        'Review by the copyright team for accuracy, completeness and common mistakes',
        'Federal E-Filing - Electronic filing with USPTO',
        'No need to wait for mail or dealing with paper files',
        'Certificate of Registration mailed directly from U.S. Copyright Office'
      ], 
      isPopular: true 
    },
    { 
      name: 'Diamond', 
      price: '149', 
      features: [
        'Everything in Gold Package',
        'Cease & Desist Letter - Customer-specific form you can customize',
        'Transfer/Assignment - Custom assignment template for selling or conveying copyright',
        '24 Hours Expedited Process of copyright application preparation'
      ] 
    }
  ];

  const whyChooseData = {
    paragraphs: [
      'Copyright protection is essential for creators who want to maintain control over their original works and prevent unauthorized use. At Unity Trademark, we simplify the copyright registration process while ensuring your application is complete, accurate, and properly classified for maximum legal protection.',
      'Our experienced copyright specialists understand the nuances of copyright law and the specific requirements of the U.S. Copyright Office. We handle all aspects of the registration process, from initial application preparation to final filing, allowing you to focus on creating while we handle the legal complexities.'
    ],
    features: [
      { title: 'Expert Application Preparation', description: 'Our copyright specialists prepare your application with meticulous attention to detail, ensuring proper work classification, complete authorship information, and accurate documentation that meets all U.S. Copyright Office requirements.' },
      { title: 'Fast Electronic Filing', description: 'We file your copyright application electronically, eliminating delays associated with paper filing and mail delivery. You receive immediate confirmation of filing and can track your application status online.' },
      { title: 'Comprehensive Legal Documents', description: 'Our Diamond package includes customizable cease and desist letters, transfer agreements, and licensing templates to help you protect and manage your copyright rights effectively.' },
      { title: 'Expedited Processing Available', description: 'Need fast turnaround? Our Diamond package offers 24-hour expedited preparation, ensuring your copyright application is ready for filing within one business day of receiving your information.' }
    ]
  };

  const testimonials = [
    { name: 'Alexandra Pierce', company: 'Author', text: 'Registering my book copyright was seamless. They handled everything professionally and I received my certificate without any issues.', image: 'https://i.pravatar.cc/150?img=5' },
    { name: 'David Kim', company: 'Musician', text: 'I have registered multiple songs with them. Their team knows exactly what they are doing and the pricing is very reasonable.', image: 'https://i.pravatar.cc/150?img=12' },
    { name: 'Lisa Rodriguez', company: 'Designer', text: 'Fast, affordable, and professional. They helped me protect my design portfolio and explained everything clearly.', image: 'https://i.pravatar.cc/150?img=47' },
    { name: 'James Anderson', company: 'Developer', text: 'The expedited service was worth it. They prepared my application in 24 hours. My software copyright is now registered.', image: 'https://i.pravatar.cc/150?img=13' },
    { name: 'Maria Santos', company: 'Photographer', text: 'I was impressed by their knowledge and attention to detail. They made sure my photographs were properly classified.', image: 'https://i.pravatar.cc/150?img=9' }
  ];

  const faqs = [
    { question: 'What types of works can be copyrighted?', answer: 'Copyright protection covers original works including literary works, musical compositions, dramatic works, choreography, pictorial and graphic works, sculptural works, motion pictures, sound recordings, architectural works, and computer software. The work must be original and fixed in a tangible medium.' },
    { question: 'How long does copyright registration take?', answer: 'Electronic copyright registration typically takes 3-8 months for processing by the U.S. Copyright Office. While copyright exists from creation, registration provides important legal benefits including the ability to file lawsuits and claim statutory damages.' },
    { question: 'Do I need to register my copyright?', answer: 'While copyright exists automatically, registration provides significant advantages. You must register before filing an infringement lawsuit, and registration allows you to claim statutory damages and attorney fees in court.' },
    { question: 'How long does copyright protection last?', answer: 'For works created after 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, anonymous works, or pseudonymous works, protection lasts 95 years from publication or 120 years from creation, whichever is shorter.' },
    { question: 'Can I copyright ideas or concepts?', answer: 'No, copyright protects the expression of ideas, not the ideas themselves. You cannot copyright titles, names, short phrases, slogans, facts, or ideas. However, you can protect the specific way you express those ideas through your creative work.' }
  ];

  return (
    <div className="service-page">
      <HeroSection {...heroData} />
      <section className="section">
        <div className="container">
          <h2 className="section-title">About Copyright Registration</h2>
          <p className="section-subtitle">
            Copyright registration provides legal protection for your original works of authorship. While copyright exists automatically upon creation, registration with the U.S. Copyright Office provides crucial legal advantages including the ability to file infringement lawsuits, claim statutory damages, and establish public record of ownership.
          </p>
        </div>
      </section>
      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our efficient three-step process ensures your copyright application is properly prepared and filed with the U.S. Copyright Office.
          </p>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="pricing" className="section">
        <div className="container">
          <h2 className="section-title">Choose Your Package</h2>
          <p className="section-subtitle">
            Select the copyright registration package that meets your needs. Both packages include professional preparation and electronic filing.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                {...plan} 
                onGetStarted={(packageName) => openModal(`Copyright Registration - ${packageName}`)}
              />
            ))}
          </div>
        </div>
      </section>
      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <div className="why-choose-content">
            {whyChooseData.paragraphs.map((paragraph, index) => (
              <p key={index} className="why-choose-paragraph">{paragraph}</p>
            ))}
          </div>
          <div className="features-grid">
            {whyChooseData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle size={40} className="feature-icon-svg" />
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Protect Your Creative Work Today</h2>
            <p className="cta-description">
              Start with professional copyright registration for just $99. Plus, take advantage of our $45 trademark registration offer to protect your brand name as well.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Copyright Registration')}
            >
              Register Your Copyright Now
            </Button>
          </div>
        </div>
      </section>
      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 25 },
                1024: { slidesPerView: 3, spaceBetween: 30 },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer"><p>{faq.answer}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CopyrightRegistration;